import { CHAIN_ID, DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { OPERATION_TX_TYPE } from '@/constants/tx';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { getGateBalanceFromChain } from '@/features/balance/api';
import { useMarginTokenInfoMap } from '@/features/chain/hook';
import { useProviderWatcher } from '@/features/web3/GlobalEffect';
import { setAppProvider } from '@/features/web3/actions';
import { useSDK } from '@/features/web3/hook';
import { queryClient } from '@/global';
import { useAppDispatch } from '@/hooks';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { useSwitchNetwork } from '@/hooks/web3/useSwitchConnectorNetwork';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { IGateBalanceInfo, IGateValueResponse } from '@/types/balance';
import { getProviderFromLocalOrSetting } from '@/utils/chain';
import { fixBalanceNumberDecimalsTo18, getTokenInfo, isNativeTokenAddr, isWrappedNativeToken } from '@/utils/token';
import { NATIVE_TOKEN_ADDRESS } from '@synfutures/sdks-perp';
import { useQuery } from '@tanstack/react-query';
import { useAsyncEffect } from 'ahooks';
import { BigNumber } from 'ethers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlastBalanceInfo } from './BlastWithdrawModal/BlastWithdrawForm';

const blastChainId = CHAIN_ID.BLAST;

const useFetchUserBlastGateValue = (chainId: number | undefined, userAddr: string | undefined) => {
  const sdk = useSDK(chainId);
  const marginTokenMap = useMarginTokenInfoMap(chainId);

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['sdk.perp.contracts.observer.getVaultBalances', chainId, userAddr],
    queryFn: async () => {
      // const apiResult = chainId && instrumentAddr && (await fetchFuturesInstrument(chainId, instrumentAddr));
      // if (!apiResult && sdk && instrumentAddr) {
      //   return fetchInstrumentFromChain(sdk, instrumentAddr);
      // }
      // return apiResult;

      if (chainId && userAddr)
        try {
          if (sdk && marginTokenMap) {
            const tokens = Object.values(marginTokenMap || {});
            // If API fails, fall back to chain request
            const chainResult = await getGateBalanceFromChain(sdk, userAddr, tokens);
            if (chainResult) {
              const balanceInfos: IGateBalanceInfo[] = [];
              chainResult.data.forEach((item) => {
                const token = getTokenInfo(item.token, chainId);
                if (token) {
                  balanceInfos.push({
                    ...token,
                    balance: WrappedBigNumber.from(
                      fixBalanceNumberDecimalsTo18(BigNumber.from(item.balance), item.token.decimals),
                    ),
                  });
                }
              });

              const result: IGateValueResponse = {
                address: userAddr,
                portfolios: balanceInfos,
                pendingDuration: 0, // TODO: not implemented
              };

              return result;
            }
          }
        } catch (chainError) {
          console.error('Both API and chain requests failed:', chainError);
          throw new Error('Failed to fetch instrument from both API and chain');
        }
      return null;
    },
    enabled: !!chainId && !!userAddr && !!sdk,
  });
};

export const useBlastWithdraw = () => {
  const [isOpen, setIsOpen] = useState(false);
  const userAddr = useUserAddr();
  const dispatch = useAppDispatch();

  useAsyncEffect(async () => {
    const providerSetting = await getProviderFromLocalOrSetting(blastChainId);
    if (providerSetting) {
      dispatch(setAppProvider({ chainId: blastChainId, provider: providerSetting.provider }));
    }
  }, [dispatch]);
  useProviderWatcher(blastChainId);
  const currentChainId = useChainId();
  const { switchWalletNetwork } = useSwitchNetwork();

  // Fetch Blast balances regardless of current chain
  const { data: gateValueResponse, isLoading, isFetched } = useFetchUserBlastGateValue(blastChainId, userAddr);

  const balances: BlastBalanceInfo[] = useMemo(() => {
    if (!gateValueResponse?.portfolios) return [];

    return gateValueResponse.portfolios
      .map((token) => ({
        token,
        balance: WrappedBigNumber.from(token.balance),
      }))
      .filter((item) => item.balance.gt(0));
  }, [gateValueResponse]);

  // Handle modal visibility based on balances and loading state
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (userAddr && isFetched) {
      if (balances.length > 0) {
        // Open once when balances are found
        if (!hasOpened) {
          setIsOpen(true);
          setHasOpened(true);
        }
      } else {
        // Close modal if no positive balances are found after loading
        if (isOpen) {
          setIsOpen(false);
        }
      }
    }
  }, [userAddr, isFetched, balances.length, hasOpened, isOpen]);

  // Reset state when user disconnects
  useEffect(() => {
    if (!userAddr) {
      setHasOpened(false);
      setIsOpen(false);
    }
  }, [userAddr]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleWithdraw = useCallback(async () => {
    if (currentChainId !== blastChainId) {
      // Switch to Blast
      try {
        await switchWalletNetwork(blastChainId);
        // After switch, the user might need to click withdraw again,
        // or we could track "pending withdraw" state.
        // For now, just switch.
      } catch (error) {
        console.error('Failed to switch chain', error);
      }
    } else {
      // Proceed with withdraw (logic to be implemented)
      console.log('Execute withdraw logic for:', balances);
    }
  }, [currentChainId, switchWalletNetwork, balances]);

  return {
    isOpen,
    balances,
    isLoading,
    onClose: handleClose,
    onWithdraw: handleWithdraw,
  };
};

export const useBlastWithdrawFooter = () => {
  const sdk = useSDK(blastChainId);
  const userAddr = useUserAddr();
  const signer = useWalletSigner();
  const { open } = useTxNotification(blastChainId);
  const { t } = useTranslation();
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const handleTxSending = useCallback(
    (txHash: string, tokenSymbol: string, tokenAmount: string) => {
      open({
        message: t(`notification.${OPERATION_TX_TYPE.WITHDRAW}.info`),
        description: `Withdrawing ${tokenAmount} ${tokenSymbol} from account to wallet`,
        tx: txHash,
        type: 'info',
        taskId: txHash,
      });
    },
    [open, t],
  );

  const handleTxSuccess = useCallback(
    (txHash: string, tokenSymbol: string, tokenAmount: string) => {
      open({
        message: t(`notification.${OPERATION_TX_TYPE.WITHDRAW}.success`),
        description: `Withdraw ${tokenAmount} ${tokenSymbol} from account to wallet`,
        tx: txHash,
        type: 'success',
        taskId: txHash,
      });
    },
    [open, t],
  );

  const handleTxError = useCallback(
    (error: string, txHash?: string) => {
      open({
        message: t(`notification.${OPERATION_TX_TYPE.WITHDRAW}.error`),
        description: `${error}`,
        tx: txHash,
        type: 'error',
        taskId: txHash,
      });
    },
    [open, t],
  );

  const handleWithdraw = useCallback(async () => {
    // Logic to handle withdraw

    if (!sdk || !userAddr || !signer) return;
    // const gate = sdk.perp.contracts.gate;

    try {
      setIsWithdrawing(true);
      const balances = await sdk.perp.observer.getGateBalances(userAddr);
      const withdrawableBalances = balances.filter((b) => b.balance.gt(0));

      if (withdrawableBalances.length === 0) {
        throw new Error('No gate balances to withdraw');
      }

      for (const token of withdrawableBalances) {
        const alignAmount = fixBalanceNumberDecimalsTo18(token.balance, token.decimals);
        let quoteToken = getTokenInfo(token, blastChainId);
        // withdraw wrapped native token, use native token as quote token
        if (isWrappedNativeToken(blastChainId, quoteToken.address)) {
          const dappChainConfig = DAPP_CHAIN_CONFIGS[blastChainId];
          quoteToken = dappChainConfig.nativeToken;
        }

        const isNativeToken = isNativeTokenAddr(quoteToken.address);

        const populatedTx = await sdk.perp.gate.withdrawWad(
          isNativeToken ? NATIVE_TOKEN_ADDRESS : quoteToken.address,
          // toWad(alignAmount),
          alignAmount,
          {
            from: userAddr,
          },
        );

        const txResponse = await signer.sendTransaction(populatedTx);
        const txHash = txResponse.hash;
        handleTxSending(txHash, quoteToken.symbol, WrappedBigNumber.from(alignAmount).formatDisplayNumber());
        const txReceipt = await txResponse.wait();
        if (txReceipt.status === 1) {
          // Transaction successful

          handleTxSuccess(txHash, quoteToken.symbol, WrappedBigNumber.from(alignAmount).formatDisplayNumber());
        } else {
          handleTxError('Withdraw transaction failed', txHash);
        }
      }

      queryClient.invalidateQueries({
        queryKey: ['sdk.perp.contracts.observer.getVaultBalances', blastChainId, userAddr],
      });

      setIsWithdrawing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('🚀 ~ useBlastWithdrawFooter ~ error:', error);
      handleTxError(error.message);
      setIsWithdrawing(false);
    }
  }, [handleTxError, handleTxSending, handleTxSuccess, sdk, signer, userAddr]);

  return {
    onWithdraw: handleWithdraw,
    isWithdrawing,
  };
};
